import React, { useState } from 'react';
import { Link } from 'react-router-dom';

interface RealEstatePartnershipsProps {
    onBack: () => void;
}

interface PartnershipOpportunity {
    companyName: string;
    type: 'Management Company' | 'Brokerage' | 'Developer' | 'Investment Firm';
    portfolioSize: string;
    focusAreas: string[];
    contactPerson: {
        name: string;
        title: string;
        email: string;
        phone: string;
    };
    partnershipBenefits: string[];
    status: 'Target' | 'Contacted' | 'Meeting Scheduled' | 'In Negotiation' | 'Partner';
    notes: string;
}

const RealEstatePartnerships: React.FC<RealEstatePartnershipsProps> = ({ onBack }) => {
    const [activeTab, setActiveTab] = useState<'companies' | 'agents' | 'outreach'>('companies');

    const partnershipOpportunities: PartnershipOpportunity[] = [
        {
            companyName: "Brookfield Properties",
            type: "Management Company",
            portfolioSize: "50M+ sq ft",
            focusAreas: ["Office", "Mixed-Use", "Retail"],
            contactPerson: {
                name: "Sarah Chen",
                title: "VP Sustainability",
                email: "s.chen@brookfieldproperties.com",
                phone: "(212) 417-7000"
            },
            partnershipBenefits: [
                "Access to 200+ NYC properties",
                "Established CAPEX budgets for energy efficiency",
                "Sustainability team already engaged with LL97",
                "Potential for white-label platform deployment"
            ],
            status: "Target",
            notes: "Large portfolio with active sustainability initiatives. Key contact identified."
        },
        {
            companyName: "Related Companies",
            type: "Developer",
            portfolioSize: "30M+ sq ft",
            focusAreas: ["Residential", "Mixed-Use", "Commercial"],
            contactPerson: {
                name: "Michael Rodriguez",
                title: "Director of Engineering",
                email: "mrodriguez@related.com",
                phone: "(212) 801-3900"
            },
            partnershipBenefits: [
                "Premier developer with high-profile projects",
                "Active in Hudson Yards and other major developments",
                "Strong focus on green building certifications",
                "Pipeline of new construction and retrofits"
            ],
            status: "Contacted",
            notes: "Initial outreach sent. Awaiting response to meeting request."
        },
        {
            companyName: "Silverstein Properties",
            type: "Developer",
            portfolioSize: "40M+ sq ft",
            focusAreas: ["Office", "Retail", "Mixed-Use"],
            contactPerson: {
                name: "Jennifer Park",
                title: "Sustainability Manager",
                email: "jpark@silversteinproperties.com",
                phone: "(212) 732-0002"
            },
            partnershipBenefits: [
                "Owner of World Trade Center properties",
                "Commitment to LEED and energy efficiency",
                "Large portfolio requiring LL97 compliance",
                "Established relationships with contractors"
            ],
            status: "Meeting Scheduled",
            notes: "Meeting scheduled for next Tuesday. Preparing deck with specific WTC analysis."
        },
        {
            companyName: "Tishman Speyer",
            type: "Management Company",
            portfolioSize: "60M+ sq ft",
            focusAreas: ["Office", "Residential", "Mixed-Use"],
            contactPerson: {
                name: "David Kim",
                title: "Chief Operating Officer",
                email: "dkim@tishmanspeyer.com",
                phone: "(212) 891-5000"
            },
            partnershipBenefits: [
                "Global real estate platform",
                "Premium office properties in Manhattan",
                "Active ESG reporting requirements",
                "Technology-forward approach to property management"
            ],
            status: "In Negotiation",
            notes: "Pilot program discussion underway. Reviewing terms for 10-building trial."
        },
        {
            companyName: "Rudin Management",
            type: "Management Company",
            portfolioSize: "20M+ sq ft",
            focusAreas: ["Office", "Residential"],
            contactPerson: {
                name: "Lisa Thompson",
                title: "VP Asset Management",
                email: "lthompson@rudin.com",
                phone: "(212) 407-2400"
            },
            partnershipBenefits: [
                "Family-owned with long-term approach",
                "Strong tenant relationships",
                "Active in Manhattan real estate",
                "Focus on property value optimization"
            ],
            status: "Partner",
            notes: "Signed partnership agreement. Platform deployment in progress for 15 buildings."
        }
    ];

    const realEstateAgents = [
        {
            name: "Marcus Foster",
            company: "Douglas Elliman",
            specialty: "Commercial Leasing",
            territory: "Midtown Manhattan",
            email: "mfoster@elliman.com",
            phone: "(212) 891-7777",
            referralPotential: "High",
            notes: "Specializes in large commercial leases. Strong relationships with building owners."
        },
        {
            name: "Amanda Wu",
            company: "CBRE",
            specialty: "Investment Sales",
            territory: "Downtown/FiDi",
            email: "amanda.wu@cbre.com",
            phone: "(212) 984-8000",
            referralPotential: "High",
            notes: "Handles $100M+ property sales. Often involved in due diligence process."
        },
        {
            name: "Robert Sterling",
            company: "JLL",
            specialty: "Property Management",
            territory: "Upper East Side",
            email: "robert.sterling@jll.com",
            phone: "(212) 351-7000",
            referralPotential: "Medium",
            notes: "Manages portfolios for institutional investors. Focus on operational efficiency."
        },
        {
            name: "Diana Chen",
            company: "Newmark",
            specialty: "Tenant Representation",
            territory: "Tribeca/SoHo",
            email: "diana.chen@newmark.com",
            phone: "(212) 372-2000",
            referralPotential: "Medium",
            notes: "Works with corporate tenants concerned about building sustainability."
        }
    ];

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Target': return 'bg-gray-100 text-gray-800';
            case 'Contacted': return 'bg-blue-100 text-blue-800';
            case 'Meeting Scheduled': return 'bg-yellow-100 text-yellow-800';
            case 'In Negotiation': return 'bg-orange-100 text-orange-800';
            case 'Partner': return 'bg-green-100 text-green-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getReferralColor = (potential: string) => {
        switch (potential) {
            case 'High': return 'bg-green-100 text-green-800';
            case 'Medium': return 'bg-yellow-100 text-yellow-800';
            case 'Low': return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={onBack}
                        className="mb-4 text-blue-600 hover:text-blue-800 flex items-center"
                    >
                        ← Back to Dashboard
                    </button>
                    <h1 className="text-4xl font-bold text-gray-900 mb-2">Real Estate Partnerships</h1>
                    <p className="text-xl text-gray-600 mb-4">
                        Strategic partnerships with property management companies and real estate professionals
                    </p>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-blue-800 text-sm">
                            <strong>🎯 Partnership Strategy:</strong> Build relationships with property managers, developers, and agents 
                            to access portfolios and generate referrals. Focus on companies with sustainability mandates and large NYC portfolios.
                        </p>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="bg-white rounded-xl shadow-lg mb-8">
                    <div className="border-b border-gray-200">
                        <nav className="flex">
                            <button
                                onClick={() => setActiveTab('companies')}
                                className={`px-6 py-4 text-sm font-medium ${
                                    activeTab === 'companies'
                                        ? 'border-b-2 border-blue-500 text-blue-600'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                🏢 Property Companies
                            </button>
                            <button
                                onClick={() => setActiveTab('agents')}
                                className={`px-6 py-4 text-sm font-medium ${
                                    activeTab === 'agents'
                                        ? 'border-b-2 border-blue-500 text-blue-600'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                👥 Real Estate Agents
                            </button>
                            <button
                                onClick={() => setActiveTab('outreach')}
                                className={`px-6 py-4 text-sm font-medium ${
                                    activeTab === 'outreach'
                                        ? 'border-b-2 border-blue-500 text-blue-600'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                📧 Outreach Tools
                            </button>
                        </nav>
                    </div>

                    <div className="p-8">
                        {/* Property Companies Tab */}
                        {activeTab === 'companies' && (
                            <div>
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-2xl font-semibold text-gray-900">Target Property Companies</h2>
                                    <div className="text-right">
                                        <div className="text-sm text-gray-600">Total Portfolio Access</div>
                                        <div className="text-2xl font-bold text-green-600">200M+ sq ft</div>
                                        <div className="text-xs text-gray-500 mt-1">NYC Commercial Real Estate</div>
                                    </div>
                                </div>

                                <div className="space-y-6">
                                    {partnershipOpportunities.map((company, index) => (
                                        <div key={index} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                                            <div className="flex justify-between items-start mb-4">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <h3 className="text-xl font-bold text-gray-900">{company.companyName}</h3>
                                                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(company.status)}`}>
                                                            {company.status}
                                                        </span>
                                                    </div>
                                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                                        <div>
                                                            <span className="text-sm font-medium text-gray-600">Type:</span>
                                                            <p className="text-gray-900">{company.type}</p>
                                                        </div>
                                                        <div>
                                                            <span className="text-sm font-medium text-gray-600">Portfolio:</span>
                                                            <p className="text-gray-900">{company.portfolioSize}</p>
                                                        </div>
                                                        <div>
                                                            <span className="text-sm font-medium text-gray-600">Focus Areas:</span>
                                                            <p className="text-gray-900">{company.focusAreas.join(', ')}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                                <div>
                                                    <h4 className="font-semibold text-gray-900 mb-2">Key Contact</h4>
                                                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                                                        <p className="font-medium text-gray-900">{company.contactPerson.name}</p>
                                                        <p className="text-sm text-gray-600">{company.contactPerson.title}</p>
                                                        <p className="text-sm text-blue-600 mt-1">{company.contactPerson.email}</p>
                                                        <p className="text-sm text-gray-600">{company.contactPerson.phone}</p>
                                                    </div>
                                                </div>
                                                
                                                <div>
                                                    <h4 className="font-semibold text-gray-900 mb-2">Partnership Benefits</h4>
                                                    <ul className="text-sm text-gray-600 space-y-1">
                                                        {company.partnershipBenefits.map((benefit, idx) => (
                                                            <li key={idx} className="flex items-start">
                                                                <span className="text-green-500 mr-2">•</span>
                                                                {benefit}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>

                                            <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                                                <h4 className="font-semibold text-yellow-800 mb-1">Notes</h4>
                                                <p className="text-yellow-700 text-sm">{company.notes}</p>
                                            </div>

                                            <div className="flex justify-end mt-4 space-x-3">
                                                <button className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm transition-colors">
                                                    View Analysis
                                                </button>
                                                <button className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-md text-sm transition-colors">
                                                    Contact
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Real Estate Agents Tab */}
                        {activeTab === 'agents' && (
                            <div>
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-2xl font-semibold text-gray-900">Real Estate Agent Network</h2>
                                    <div className="text-right">
                                        <div className="text-sm text-gray-600">Active Relationships</div>
                                        <div className="text-2xl font-bold text-blue-600">{realEstateAgents.length}</div>
                                        <div className="text-xs text-gray-500 mt-1">Commercial Agents</div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {realEstateAgents.map((agent, index) => (
                                        <div key={index} className="bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                                            <div className="flex justify-between items-start mb-3">
                                                <div>
                                                    <h3 className="text-lg font-bold text-gray-900">{agent.name}</h3>
                                                    <p className="text-blue-600 font-medium">{agent.company}</p>
                                                </div>
                                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getReferralColor(agent.referralPotential)}`}>
                                                    {agent.referralPotential} Potential
                                                </span>
                                            </div>
                                            
                                            <div className="space-y-2 mb-4">
                                                <div>
                                                    <span className="text-sm font-medium text-gray-600">Specialty:</span>
                                                    <p className="text-gray-900">{agent.specialty}</p>
                                                </div>
                                                <div>
                                                    <span className="text-sm font-medium text-gray-600">Territory:</span>
                                                    <p className="text-gray-900">{agent.territory}</p>
                                                </div>
                                                <div>
                                                    <span className="text-sm font-medium text-gray-600">Contact:</span>
                                                    <p className="text-blue-600 text-sm">{agent.email}</p>
                                                    <p className="text-gray-600 text-sm">{agent.phone}</p>
                                                </div>
                                            </div>

                                            <div className="bg-gray-50 rounded-lg p-3 mb-4">
                                                <p className="text-sm text-gray-700">{agent.notes}</p>
                                            </div>

                                            <div className="flex space-x-2">
                                                <button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded text-sm transition-colors">
                                                    Contact
                                                </button>
                                                <button className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded text-sm transition-colors">
                                                    View Referrals
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Outreach Tools Tab */}
                        {activeTab === 'outreach' && (
                            <div>
                                <h2 className="text-2xl font-semibold text-gray-900 mb-6">Outreach Tools & Templates</h2>
                                
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Email Templates</h3>
                                        <div className="space-y-4">
                                            <div className="bg-white rounded-lg p-4 border border-gray-200">
                                                <h4 className="font-medium text-gray-900 mb-2">Initial Partnership Outreach</h4>
                                                <p className="text-sm text-gray-600 mb-3">Cold outreach template for property management companies</p>
                                                <button className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded text-sm transition-colors">
                                                    Use Template
                                                </button>
                                            </div>
                                            
                                            <div className="bg-white rounded-lg p-4 border border-gray-200">
                                                <h4 className="font-medium text-gray-900 mb-2">Follow-up After Demo</h4>
                                                <p className="text-sm text-gray-600 mb-3">Follow-up template after platform demonstration</p>
                                                <button className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded text-sm transition-colors">
                                                    Use Template
                                                </button>
                                            </div>
                                            
                                            <div className="bg-white rounded-lg p-4 border border-gray-200">
                                                <h4 className="font-medium text-gray-900 mb-2">Referral Program Invitation</h4>
                                                <p className="text-sm text-gray-600 mb-3">Template for inviting agents to referral program</p>
                                                <button className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded text-sm transition-colors">
                                                    Use Template
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Partnership Materials</h3>
                                        <div className="space-y-4">
                                            <div className="bg-white rounded-lg p-4 border border-gray-200">
                                                <h4 className="font-medium text-gray-900 mb-2">Partnership One-Pager</h4>
                                                <p className="text-sm text-gray-600 mb-3">Executive summary of partnership benefits and terms</p>
                                                <button className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded text-sm transition-colors">
                                                    Download PDF
                                                </button>
                                            </div>
                                            
                                            <div className="bg-white rounded-lg p-4 border border-gray-200">
                                                <h4 className="font-medium text-gray-900 mb-2">ROI Calculator</h4>
                                                <p className="text-sm text-gray-600 mb-3">Interactive tool showing partnership value for property companies</p>
                                                <button className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded text-sm transition-colors">
                                                    Open Tool
                                                </button>
                                            </div>
                                            
                                            <div className="bg-white rounded-lg p-4 border border-gray-200">
                                                <h4 className="font-medium text-gray-900 mb-2">Case Study Collection</h4>
                                                <p className="text-sm text-gray-600 mb-3">Success stories from existing partnerships</p>
                                                <button className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded text-sm transition-colors">
                                                    View Cases
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-8 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Partnership Metrics</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        <div>
                                            <h4 className="font-medium text-gray-700 mb-2">Outreach Performance</h4>
                                            <ul className="text-sm text-gray-600 space-y-1">
                                                <li>• 45% email open rate</li>
                                                <li>• 12% response rate</li>
                                                <li>• 8% meeting conversion</li>
                                                <li>• 25% partnership close rate</li>
                                            </ul>
                                        </div>
                                        <div>
                                            <h4 className="font-medium text-gray-700 mb-2">Pipeline Value</h4>
                                            <ul className="text-sm text-gray-600 space-y-1">
                                                <li>• $2.5M total pipeline</li>
                                                <li>• $450K committed partnerships</li>
                                                <li>• $1.2M in active negotiations</li>
                                                <li>• 180 day avg. sales cycle</li>
                                            </ul>
                                        </div>
                                        <div>
                                            <h4 className="font-medium text-gray-700 mb-2">Referral Program</h4>
                                            <ul className="text-sm text-gray-600 space-y-1">
                                                <li>• 25 active agent referrers</li>
                                                <li>• $75K total referral fees paid</li>
                                                <li>• 3.2 referrals per agent/month</li>
                                                <li>• 35% referral conversion rate</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <Link
                        to="/chinese-building-owners"
                        className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
                    >
                        <div className="text-center">
                            <div className="text-2xl mb-3">🇨🇳</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Chinese Building Owners</h3>
                            <p className="text-sm text-gray-600">Major Chinese-owned portfolios</p>
                        </div>
                    </Link>
                    
                    <Link
                        to="/lender-partnerships"
                        className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
                    >
                        <div className="text-center">
                            <div className="text-2xl mb-3">💰</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Lender Partnerships</h3>
                            <p className="text-sm text-gray-600">CPACE financing and loan partnerships</p>
                        </div>
                    </Link>
                    
                    <Link
                        to="/contractor-partnerships"
                        className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
                    >
                        <div className="text-center">
                            <div className="text-2xl mb-3">🔧</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">Contractor Network</h3>
                            <p className="text-sm text-gray-600">Vetted contractors and service providers</p>
                        </div>
                    </Link>
                    
                    <Link
                        to="/cto-recruitment"
                        className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
                    >
                        <div className="text-center">
                            <div className="text-2xl mb-3">👨‍💻</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">CTO Recruitment</h3>
                            <p className="text-sm text-gray-600">Technical leadership hiring process</p>
                        </div>
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default RealEstatePartnerships;
